import { RefObject, useEffect, useState } from 'react';

interface UseScrollPositionReturn {
	isAtTop: boolean;
	isAtBottom: boolean;
}

/**
 * Custom hook to track scroll position of a container element.
 * Returns whether the container is scrolled to the top or bottom.
 *
 * @param containerRef - React ref to the scrollable container element
 * @param dependencies - Optional array of dependencies to trigger re-check
 * @param threshold - Optional threshold in pixels for top/bottom detection (default: 5)
 * @returns Object with isAtTop and isAtBottom boolean flags
 */
export function useScrollPosition(containerRef: RefObject<HTMLElement | null>, dependencies: unknown[] = [], threshold: number = 5): UseScrollPositionReturn {
	const [isAtTop, setIsAtTop] = useState(true);
	const [isAtBottom, setIsAtBottom] = useState(false);

	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;

		const handleScroll = () => {
			const { scrollTop, scrollHeight, clientHeight } = container;
			const atTop = scrollTop <= threshold;
			const atBottom = scrollTop + clientHeight >= scrollHeight - threshold;

			setIsAtTop(atTop);
			setIsAtBottom(atBottom);
		};

		// Initial check
		handleScroll();

		container.addEventListener('scroll', handleScroll);
		return () => container.removeEventListener('scroll', handleScroll);
	}, [containerRef, threshold, JSON.stringify(dependencies)]);

	return { isAtTop, isAtBottom };
}
